import { filterProducts, normalizeSearch } from "./catalog";
import { hasBroadPersonalSelection } from "./filter-visit";
import { PERSONAL_CATEGORIES_FILTER, PERSONAL_STORES_FILTER } from "./personal-filters";
import { productSearchKey } from "./product-search";
import type { ProductFilters, ProductView } from "./types";

export const SEARCH_RECOVERY_THRESHOLD = 4;
export const SEARCH_RECOVERY_PREVIEW_SIZE = 4;

export type SearchRecoveryScope = "personal" | "all";
export type SearchRecoveryLabel = "personalStores" | "personalCategories" |
  "personalBoth" | "otherStores" | "otherCategories" | "otherCatalogs" | "otherFilters";

export interface SearchRecoveryGroup {
  scope: SearchRecoveryScope;
  label: SearchRecoveryLabel;
  products: ProductView[];
  /** Full destination count, including any matches already in the primary group. */
  destinationCount: number;
  filters: ProductFilters;
}

export interface SearchRecovery {
  groups: SearchRecoveryGroup[];
  /** All visible matching offers, not the sum of truncated previews. */
  totalMatches: number;
}

/** Same query/search ranking, broader scopes. No API requests or invented matches.
 * The caller supplies the already date-validated catalog and primary result set.
 */
export function buildSearchRecovery(
  products: ProductView[],
  filters: ProductFilters,
  primary: readonly ProductView[],
): SearchRecovery {
  const empty = { groups: [], totalMatches: primary.length };
  if (!normalizeSearch(filters.query) || primary.length > SEARCH_RECOVERY_THRESHOLD ||
    (filters.store === "all" && filters.categoryId === "all" && filters.catalogId === "all")) {
    return empty;
  }

  const broadFilters: ProductFilters = {
    ...filters, store: "all", categoryId: "all", catalogId: "all",
  };
  // Reuse the existing default visibility policy (including alcohol exclusion).
  const broad = filterProducts(products, broadFilters);
  const seen = new Set(primary.map(productSearchKey));
  const outside = broad.filter((product) => !seen.has(productSearchKey(product)));
  if (!outside.length) return empty;

  const groups: SearchRecoveryGroup[] = [];
  const myStores = hasBroadPersonalSelection(filters.personalStores);
  const myCategories = hasBroadPersonalSelection(filters.personalCategoryIds);
  if (myStores || myCategories) {
    const storeSet = new Set(filters.personalStores ?? []);
    const categorySet = new Set(filters.personalCategoryIds ?? []);
    const inPreferences = (product: ProductView) =>
      (!myStores || storeSet.has(product.catalog.chain)) &&
      (!myCategories || categorySet.has(product.category_id ?? ""));
    const preferred = outside.filter(inPreferences);
    if (preferred.length) {
      const preferredFilters: ProductFilters = {
        ...broadFilters,
        store: myStores ? PERSONAL_STORES_FILTER : "all",
        categoryId: myCategories ? PERSONAL_CATEGORIES_FILTER : "all",
      };
      groups.push({
        scope: "personal",
        label: myStores && myCategories ? "personalBoth" : myStores ? "personalStores" : "personalCategories",
        products: preferred,
        destinationCount: filterProducts(products, preferredFilters).length,
        filters: preferredFilters,
      });
      // Deduplicate the entire group, not just the preview.
      preferred.forEach((product) => seen.add(productSearchKey(product)));
    }
  }

  const rest = broad.filter((product) => !seen.has(productSearchKey(product)));
  if (rest.length) {
    const storeNarrowed = filters.store !== "all";
    const categoryNarrowed = filters.categoryId !== "all";
    groups.push({
      scope: "all",
      label: storeNarrowed && !categoryNarrowed && filters.catalogId === "all" ? "otherStores" :
        categoryNarrowed && !storeNarrowed && filters.catalogId === "all" ? "otherCategories" :
        !storeNarrowed && !categoryNarrowed ? "otherCatalogs" : "otherFilters",
      products: rest,
      destinationCount: broad.length,
      filters: broadFilters,
    });
  }
  return { groups, totalMatches: new Set([...primary, ...broad].map(productSearchKey)).size };
}
